import { useCallback, useEffect, useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import { Alert, Button, CircularProgress, IconButton } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useUnit } from 'effector-react';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { ControlledAutocompleteField } from '../../../../components/FormControlsV2/ControlledAutocompleteField';
import { ControlledTextField } from '../../../../components/FormControlsV2/ControlledTextField';
import { FormProvider } from '../../../../components/FormControlsV2/FormProvider';
import { ERRORS_TEXTS } from '../../../../const/validation';
import { Faq$, faqApi } from '../../../../effector/faq';
import { Question } from '../../../../types/faq';
import { SortType } from '../../../../types/pagination';
import { getMessageFromFailedRequest } from '../../../../utils';

type FormFields = {
  title: string;
  text: string;
  strategyId: string;
};

const schema = Yup.object({
  title: Yup.string().default('').required(ERRORS_TEXTS.required),
  text: Yup.string().default('').required(ERRORS_TEXTS.required),
  strategyId: Yup.string().default(''),
});

interface Props {
  question?: Question | null;
  onClose: () => void;
  onUpdate: () => void;
}

export const QuestionDialog: React.FC<Props> = ({ question, onClose, onUpdate }) => {
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { strategies } = useUnit(Faq$);
  const { enqueueSnackbar } = useSnackbar();

  const isEdit = !!question;

  const strategyOptions = useMemo(() => strategies.map(({ id, name }) => ({ value: id, label: name })), [strategies]);

  const defaultValues = useMemo<FormFields>(
    () => ({
      title: question?.title ?? '',
      text: question?.text ?? '',
      strategyId: question?.parameters?.strategyId ?? '',
    }),
    [question],
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<FormFields>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const handleError = useCallback((e: Error) => {
    const message = getMessageFromFailedRequest(e);
    setError(message);
  }, []);

  const onSubmit = useCallback(
    async (values: FormFields) => {
      setError('');
      setIsLoading(true);

      const data = {
        title: values.title,
        text: values.text,
        parameters: values.strategyId ? { strategyId: values.strategyId } : ({} as Record<string, string>),
      };

      try {
        if (isEdit) {
          await faqApi.editQuestion({ ...data, id: question!.id } as Question);
          enqueueSnackbar('Вопрос обновлен');
        } else {
          await faqApi.addQuestion(data);
          enqueueSnackbar('Вопрос добавлен');
        }
        onUpdate();
      } catch (e) {
        handleError(e as Error);
      } finally {
        setIsLoading(false);
      }
    },
    [isEdit, question, enqueueSnackbar, onUpdate, handleError],
  );

  useEffect(() => {
    faqApi.getStrategies({ pageNumber: 0, pageSize: 300, sortFieldName: 'name', sortDirection: SortType.Ask });
  }, []);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth disableEnforceFocus>
      <DialogTitle align="center">{isEdit ? 'Редактирование вопроса' : 'Добавление вопроса'}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <FormProvider control={control} errors={errors}>
            <ControlledTextField name="title" label="Заголовок" />
            <ControlledTextField name="text" label="Текст" multiline rows={4} />
            {strategyOptions.length > 0 && (
              <ControlledAutocompleteField name="strategyId" label="Стратегия" options={strategyOptions} />
            )}
          </FormProvider>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!isDirty || isLoading}
            fullWidth
            sx={{ mt: 2 }}
            startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
          >
            Сохранить
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
